import { type MutableRefObject, useCallback } from 'react';
import { MasonryEngineNodeAction } from '@leagueplatform/masonry-engine';
import { useInvokeAction } from '../hooks/use-invoke-action.hook';

// Internal interface for createFlowStepNavigation function
interface UseFlowStepNavigationOptions {
  currentStepIndexRef: MutableRefObject<number>;
  flowNodeId: string;
  numSteps: number;
  onTrailingButtonClick?: MasonryEngineNodeAction;
  onLeadingButtonClick?: MasonryEngineNodeAction;
  onComplete?: MasonryEngineNodeAction;
  setTransitioningDirection?: (direction: 1 | -1 | 0) => void;
}

export type FlowNavigationFn = (direction: 1 | -1) => Promise<void>;

/**
 * Creates a flow step navigation function given various
 */
export const useFlowNavigationFn = ({
  currentStepIndexRef,
  numSteps,
  flowNodeId,
  onTrailingButtonClick,
  onLeadingButtonClick,
  onComplete,
  setTransitioningDirection,
}: UseFlowStepNavigationOptions): FlowNavigationFn => {
  const invokeAction = useInvokeAction();

  return useCallback(
    async (direction) => {
      const currentStepIndex = currentStepIndexRef.current;
      const newStepIndex = currentStepIndex + direction;

      // Determine which action will be used based on direction
      let actionToInvoke: MasonryEngineNodeAction | undefined;

      // Special case: if we're on the last step and trying to go forward, use completion action
      if (direction === 1 && currentStepIndex === numSteps - 1) {
        actionToInvoke = onComplete;
      } else if (direction === 1 && onTrailingButtonClick) {
        actionToInvoke = onTrailingButtonClick;
      } else if (direction === -1 && onLeadingButtonClick) {
        actionToInvoke = onLeadingButtonClick;
      }

      // Set transitioning state if we have a transition action
      const shouldTransition = Boolean(actionToInvoke);

      if (shouldTransition) {
        setTransitioningDirection?.(direction);
      }

      try {
        // Handle completion case
        if (direction === 1 && currentStepIndex === numSteps - 1) {
          if (onComplete) {
            await invokeAction(onComplete);
          }
        } else {
          // Handle step transition case
          let shouldUpdateStepIndex = true;
          if (actionToInvoke) {
            const transitionResult = await invokeAction(actionToInvoke);

            // Prevent updating the step index if the transition action failed
            if (transitionResult?.isSuccess === false) {
              shouldUpdateStepIndex = false;
            }
          }

          // If the transition action did not return `false`, which is how
          // actions like `submitFlowStep` indicate they failed,
          // we can update the flow state.
          if (shouldUpdateStepIndex) {
            // Update the parent flow node's currentStepIndex property
            await invokeAction({
              type: 'setNodeState',
              payload: {
                nodeId: flowNodeId,
                state: {
                  currentStepIndex: newStepIndex,
                },
              },
              namespace: 'default',
            });
          }
        }
      } finally {
        // Always reset transitioning state if it was set
        if (shouldTransition) {
          setTransitioningDirection?.(0);
        }
      }
    },
    [
      currentStepIndexRef,
      flowNodeId,
      invokeAction,
      onComplete,
      onLeadingButtonClick,
      onTrailingButtonClick,
      numSteps,
      setTransitioningDirection,
    ],
  );
};
